// src/lib/navigation.js
//
// Central registry of sidebar navigation routes for the Lift app.
//
// This is the single source of truth for what shows up in the sidebar.
// Sidebar.js just renders whatever is exported here — it never needs to
// change again to support a new page or a new app.
//
// Structure: NAV_GROUPS is an array of groups. Each group is:
//   {
//     label: string   // section heading shown above the group ('' = no heading)
//     items: [
//       { icon: <SomeIcon />, label: 'Page Name', path: '/management/page' },
//       ...
//     ]
//   }
//
// Every group below has a label on purpose — giving every section the same
// "heading + indented items" treatment is what makes it visually obvious
// that, e.g., Calendar/Groups View/Expenses/Meetings History/Working Days
// all belong to Diary, the same way Transactions/Receipts read as
// belonging to Payments. A one-item group (Groups, Loans) still gets a
// heading for the same reason, even though there's only one link under it
// today — more items can be dropped under it later without restructuring.
//
// Note: the sidebar labels here are a pure re-grouping/re-labeling of the
// EXISTING top-level routes (transactions, groups, users, loans, requests,
// receipts, fieldwork, metrics, admin) in App.js — none of those route
// paths changed, only how they're organized and what they're called in
// the sidebar. That keeps this a nav/IA change without touching routing
// that other parts of the app may depend on. Diary is the one exception:
// its sub-pages already live at nested /management/diary/* routes (see
// App.js) because they used to be internal tabs, not separate pages.
//
// HOW TO ADD ROUTES FOR A NEW APP
// --------------------------------
// When we start building the next app, don't touch Sidebar.js. Instead:
//
//   1. Add a new group below, e.g.:
//        {
//          label: 'Payroll',
//          items: [
//            { icon: <SomeIcon />, label: 'Payslips', path: '/management/payroll/payslips' },
//            { icon: <SomeIcon />, label: 'Employees', path: '/management/payroll/employees' },
//          ],
//        },
//
//   2. Register the matching <Route> element(s) in App.js.

import {
  Receipt as ReceiptIcon,
  Person as UserIcon,
  Groups as GroupsIcon,
  CalendarToday as CalendarIcon,
  BarChart as MetricsIcon,
  Settings as ConfigurationsIcon,
  Settings as WorkingDaysIcon,
  History as HistoryIcon,
  AccountBalance as LoansIcon,
  Assignment as RequestsIcon,
  WorkOutline as FieldworkIcon,
  TableChart as LongTermLoansIcon,
  Savings as AllocationsIcon,
  Bolt as AdvanceLoansIcon,
  Assessment as PerformancePeriodsIcon,
  ArrowUpward as OutgoingPaymentsIcon,
  PlayArrow as ExecuteRequestsIcon,
  QueryStats as FinancialReportsIcon,
} from '@mui/icons-material';
import TransactionsIcon from '../components/TransactionsIcon';

export const NAV_GROUPS = [
  {
    label: 'Payments',
    items: [
      // Renamed from "Transactions" — same route, just a clearer label.
      { icon: <TransactionsIcon />, label: 'Incoming Payments', path: '/management/transactions' },
      // Was the "Bank Transactions" tab inside Fieldwork.js
      // (ConsolidatedWithdrawals); it's now its own page here instead.
      { icon: <OutgoingPaymentsIcon />, label: 'Outgoing Payments', path: '/management/payments/outgoing' },
      { icon: <ReceiptIcon />, label: 'Receipts', path: '/management/receipts' },
    ],
  },
  {
    label: 'Groups',
    items: [
      { icon: <GroupsIcon />, label: 'All Groups', path: '/management/groups' },
      { icon: <PerformancePeriodsIcon />, label: 'Performance Periods', path: '/management/groups/performance-periods' },
    ],
  },
  {
    label: 'Users',
    items: [
      { icon: <UserIcon />, label: 'All Members', path: '/management/users' },
      { icon: <AllocationsIcon />, label: 'Member Allocations', path: '/management/users/allocations' },
      { icon: <RequestsIcon />, label: 'Member Requests', path: '/management/requests' },
      // Was the "Execute Requests" tab inside Fieldwork.js; it's now its
      // own page here instead, right below Member Requests.
      { icon: <ExecuteRequestsIcon />, label: 'Execute Requests', path: '/management/users/execute-requests' },
      { icon: <FieldworkIcon />, label: 'Fieldwork', path: '/management/fieldwork' },
    ],
  },
  {
    label: 'Loans',
    items: [
      { icon: <LoansIcon />, label: 'Applications', path: '/management/loans' },
      // This used to be the "Table" button inside Loans.js, toggled with
      // local state; it's now its own route (see App.js) so it can be
      // linked to directly from here.
      { icon: <LongTermLoansIcon />, label: 'Long-term Loans', path: '/management/loans/long-term' },
      { icon: <AdvanceLoansIcon />, label: 'Advance Loans', path: '/management/loans/advance' },
    ],
  },
  {
    // The Diary app's own pages, nested under one section heading — these
    // used to be internal tabs inside Diary.js; they're now real routes
    // (see App.js) so the sidebar can link straight to each one.
    label: 'Diary',
    items: [
      { icon: <CalendarIcon />, label: 'Calendar', path: '/management/diary/calendar' },
      { icon: <GroupsIcon />, label: 'Groups View', path: '/management/diary/groups' },
      { icon: <ReceiptIcon />, label: 'Expenses', path: '/management/diary/expenses' },
      { icon: <HistoryIcon />, label: 'Meetings History', path: '/management/diary/history' },
      { icon: <WorkingDaysIcon />, label: 'Working Days', path: '/management/diary/working-days' },
    ],
  },
  {
    // Company revenue, loan and advance portfolio reports, with Excel
    // downloads (components/Financials/ReportsDashboard.js).
    label: 'Financials',
    items: [
      { icon: <FinancialReportsIcon />, label: 'Reports', path: '/management/financials/reports' },
    ],
  },
  {
    // Sits at the very bottom of the sidebar, as requested.
    label: 'Administration',
    items: [
      { icon: <MetricsIcon />, label: 'Metrics', path: '/management/metrics' },
      { icon: <ConfigurationsIcon />, label: 'Configurations', path: '/management/admin' },
    ],
  },
];